'use client';

import { useEffect, useState, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────
 * ConsentBanner
 * ─────────────────────────────────────────────────────────
 * Minimaler Audio-Consent: Nur Voice.png Bild.
 * Erscheint NUR wenn ein Video beim Hover keinen Ton
 * abspielen kann (Event: 'request-audio-consent').
 *
 * Klick auf Bild → Audio entsperrt + Consent gespeichert.
 * ───────────────────────────────────────────────────────── */

// ── Consent Utilities ──

/** Prüft ob Audio-Consent bereits erteilt wurde (nur localStorage, keine Cookies!) */
export function hasAudioConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('soundAllowed') === '1';
  } catch {
    return false;
  }
}

/** Speichert Audio-Consent nur in localStorage (keine Cookies — Chrome blockiert diese!) */
function persistConsent(val: '1' | '0') {
  if (typeof window === 'undefined') return;
  try {
    if (val === '1') {
      localStorage.setItem('soundAllowed', val);
    } else {
      localStorage.removeItem('soundAllowed');
    }
  } catch {
    // Storage nicht verfügbar — stiller Fehler
  }
}

/** Entfernt Audio-Consent (für Settings/Testing) */
export function removeAudioConsent() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('soundAllowed');
    (window as any).__audioUnlocked = false;
  } catch {
    // stiller Fehler
  }
}

// ── AudioContext Unlock (V1-Technik) ──
async function unlockAudioContext(): Promise<boolean> {
  try {
    const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioCtx) return false;

    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') await ctx.resume();

    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);

    (window as any).__audioUnlocked = true;

    src.onended = () => {
      src.disconnect();
      ctx.close().catch(() => {});
    };

    return true;
  } catch {
    return false;
  }
}

// ── Globale Hilfsfunktionen (Browser) ──
if (typeof window !== 'undefined') {
  (window as any).removeAudioConsent = removeAudioConsent;
  (window as any).checkAudioConsent = hasAudioConsent;
}

// ── Komponente ──

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  // Nur auf 'request-audio-consent' Event reagieren (von Video-Card getriggert)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onRequest = () => {
      // Dialog anzeigen wenn Audio NICHT entsperrt ist.
      // hasAudioConsent() (localStorage) reicht NICHT —
      // der Browser braucht eine User-Geste in JEDER Session.
      if ((window as any).__audioUnlocked) return; // Audio bereits entsperrt
      setVisible(true);
    };

    document.addEventListener('request-audio-consent', onRequest);
    return () => document.removeEventListener('request-audio-consent', onRequest);
  }, []);

  const allowSound = useCallback(async () => {
    // 1) AudioContext entsperren (echte User-Geste!)
    await unlockAudioContext();

    // 2) Consent speichern
    persistConsent('1');

    // 3) App informieren
    document.dispatchEvent(new Event('sound-allowed'));

    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-white/15 backdrop-blur-sm backdrop-saturate-50 backdrop-contrast-75"
        onClick={allowSound}
        aria-hidden="true"
      />

      {/* Nur Voice.png — Klick = Akzeptieren */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <button
          type="button"
          onClick={allowSound}
          className="cursor-pointer transition-transform duration-200 hover:scale-105 focus:outline-none"
          aria-label="Audio aktivieren"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/Voice.png?v=20260328"
            alt="Audio aktivieren"
            className="max-h-100 w-auto object-contain sm:max-h-116"
          />
        </button>
      </div>
    </>
  );
}
